/* ------------------------------------------------------------
   BANK TRANSFER OFFER — behaviour
   ------------------------------------------------------------
   Asks n8n to raise a draft order the customer can pay by bank transfer, and
   reports back inline. Never touches the cart and never blocks Checkout.

   ONE DELEGATED LISTENER, BOUND ONCE.

   The cart drawer rebuilds its own footer with innerHTML on every cart change,
   and scripts inserted that way never execute. Anything bound directly to these
   buttons would therefore work exactly once and then silently stop. So this
   binds a single click listener to the document and finds its way up to the
   block from whatever was clicked — which keeps working no matter how many
   times the drawer redraws.

   THE CART IS READ AT SEND TIME, NOT FROM THE MARKUP.

   The rendered total can be stale by the time someone types their address —
   they may have changed a quantity in another tab, or the drawer may not have
   caught up. /cart.js is the authoritative answer, so the request carries what
   the cart actually contains at the moment of sending. The figure on screen is
   only ever an invitation.
   ------------------------------------------------------------ */
(function () {
  'use strict';

  var ENDPOINT = 'https://buildagym.app.n8n.cloud/webhook/bank-transfer-quote';

  /* Matches the threshold in ctx-bank-transfer-offer.liquid. Checked again here
     because the cart can drop below it after the block was rendered. */
  var THRESHOLD_CENTS = 1000000;

  /* Deliberately permissive. Rejecting odd but valid addresses on a $12,000
     order costs far more than letting a typo through, which the customer will
     notice when nothing arrives. */
  function looksLikeEmail(v) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(v || '').trim());
  }

  function money(cents) {
    return '$' + (cents / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  function setStatus(root, tone, message) {
    var el = root.querySelector('[data-ctx-bt-status]');
    if (!el) return;
    el.textContent = message;
    el.setAttribute('data-tone', tone);
    el.hidden = false;
  }

  /* One block can be sent once per page view. Without this, an impatient double
     click raises two draft orders for the same cart and someone reconciles them
     by hand later. */
  var sentFrom = new WeakSet();

  function send(root) {
    if (sentFrom.has(root)) return;

    var input = root.querySelector('[data-ctx-bt-input]');
    var stateEl = root.querySelector('[data-ctx-bt-state]');
    var button = root.querySelector('[data-ctx-bt-send]');
    var email = input ? input.value.trim() : '';
    var state = stateEl ? stateEl.value.trim() : '';

    if (!looksLikeEmail(email)) {
      setStatus(root, 'error', 'That email address does not look right — please check it.');
      if (input) input.focus();
      return;
    }

    /* Without a destination Shopify cannot quote freight, and freight is the
       difference between a payable invoice and a promise to follow up. */
    if (!state) {
      setStatus(root, 'error', 'Pick the delivery state so we can quote your freight.');
      if (stateEl) stateEl.focus();
      return;
    }

    if (button) { button.disabled = true; button.textContent = 'Sending…'; }
    setStatus(root, 'ok', 'Preparing your invoice…');

    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !cart.items || !cart.items.length) {
          throw new Error('empty-cart');
        }
        if (cart.total_price < THRESHOLD_CENTS) {
          throw new Error('below-threshold');
        }

        var rateBp = parseInt(root.getAttribute('data-bt-rate-bp'), 10) || 250;

        return fetch(ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: email,
            state: state,
            rate_bp: rateBp,
            cart_token: cart.token,
            currency: cart.currency,
            /* Pre-tax, pre-freight. n8n recomputes the discount from the draft
               once freight is on it — this is context, not the final figure. */
            cart_total_cents: cart.total_price,
            item_count: cart.item_count,
            /*
              Both prices, deliberately.

              The draft order is rebuilt from these lines, and Shopify does NOT
              re-run automatic discounts when it builds one — so a cart carrying
              the builder's bundle discount came out of this at full price.
              Measured on a real cart: $85,951 became a $93,242 invoice, about
              $7,300 of the customer's discount quietly gone.

              Sending what the line ACTUALLY costs alongside what it would have
              cost lets the draft mirror the cart exactly, whatever produced the
              difference — the bundle function, an automatic discount, or a code
              the customer typed before clicking. Re-deriving it would mean
              reimplementing every discount rule and getting it wrong the first
              time one changes.
            */
            items: cart.items.map(function (i) {
              return {
                variant_id: i.variant_id,
                product_id: i.product_id,
                quantity: i.quantity,
                title: i.title,
                sku: i.sku,
                original_line_price_cents: i.original_line_price,
                final_line_price_cents: (i.final_line_price != null ? i.final_line_price : i.line_price),
                /* _bundle_id drives the discount function and _covers ties BAG
                   Care+ to the machine it covers. Anything rebuilding the cart
                   has to carry them or those links are severed. */
                properties: i.properties || null
              };
            }),
            note: cart.note || '',
            page: window.location.pathname,
            requested_at: new Date().toISOString()
          })
        });
      })
      .then(function (res) {
        if (!res || !res.ok) throw new Error('endpoint-' + (res ? res.status : 'no-response'));
        sentFrom.add(root);

        /* Collapse to a single line rather than hiding the button and leaving
           everything else in place. The first version did that and the block got
           TALLER on success — the footer grew and pushed "View full cart" and
           "Remove all" off the bottom of the drawer. The confirmation replaces
           the offer; it does not sit underneath it. */
        root.classList.add('ctx-bt--sent');
        var form = root.querySelector('[data-ctx-bt-form]');
        if (form) form.hidden = true;
        var open = root.querySelector('[data-ctx-bt-open]');
        if (open) open.hidden = true;
        var head = root.querySelector('.ctx-bt__head');
        if (head) head.hidden = true;

        setStatus(root, 'ok', 'Invoice sent to ' + email + '. Freight follows on it.');
      })
      .catch(function (err) {
        if (button) { button.disabled = false; button.textContent = 'Send invoice'; }
        var reason = String(err && err.message);
        if (reason === 'empty-cart') {
          setStatus(root, 'error', 'Your cart looks empty — add something first.');
        } else if (reason === 'below-threshold') {
          setStatus(
            root,
            'error',
            'Bank transfer invoices start at ' + money(THRESHOLD_CENTS) +
            '. Your cart has changed since this offer appeared.'
          );
        } else {
          setStatus(
            root,
            'error',
            'We could not send that just now. Call (512) 591-8553 and we will raise the invoice for you.'
          );
        }
      });
  }

  document.addEventListener('click', function (e) {
    var openBtn = e.target.closest && e.target.closest('[data-ctx-bt-open]');
    if (openBtn) {
      var root = openBtn.closest('[data-ctx-bt]');
      if (!root) return;
      var form = root.querySelector('[data-ctx-bt-form]');
      if (form) {
        form.hidden = false;
        var input = form.querySelector('[data-ctx-bt-input]');
        /* A known customer has their address prefilled, so send them straight to
           the action rather than to a field they do not need to touch. */
        if (input && !input.value) input.focus();
      }
      openBtn.hidden = true;
      return;
    }

    var sendBtn = e.target.closest && e.target.closest('[data-ctx-bt-send]');
    if (sendBtn) {
      var sendRoot = sendBtn.closest('[data-ctx-bt]');
      if (sendRoot) send(sendRoot);
    }
  });

  /* Enter in the email field should send, not submit the cart form it sits
     inside — which on the cart page would navigate to checkout. */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    var input = e.target.closest && e.target.closest('[data-ctx-bt-input]');
    if (!input) return;
    e.preventDefault();
    var root = input.closest('[data-ctx-bt]');
    if (root) send(root);
  });
})();
